import React from 'react';
import { Link } from 'react-router-dom';

export type InsightTone = 'info' | 'warning' | 'danger';

export interface QuickInsight {
  id: string;
  tone: InsightTone;
  title: string;
  body: string;
  /** Optional "→ jump there" link. Internal route only. */
  to?: string;
  ctaLabel?: string;
}

interface QuickInsightsPanelProps {
  insights: QuickInsight[];
}

const IconBulb = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M12 2a7 7 0 0 0-4 12.74V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.26A7 7 0 0 0 12 2z" />
  </svg>
);

const IconWarn = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IconInfo = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const IconError = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

function toneIcon(tone: InsightTone): React.ReactNode {
  switch (tone) {
    case 'danger':
      return <IconError />;
    case 'warning':
      return <IconWarn />;
    default:
      return <IconInfo />;
  }
}

const QuickInsightsPanel: React.FC<QuickInsightsPanelProps> = ({ insights }) => {
  if (!insights.length) return null;

  return (
    <section className="dash-insights" aria-labelledby="dash-insights-title">
      <div className="dash-insights-head">
        <h2 className="dash-insights-title" id="dash-insights-title">
          <IconBulb />
          De atenție acum
        </h2>
        <span className="dash-insights-sub">
          {insights.length} {insights.length === 1 ? 'observație' : 'observații'}
        </span>
      </div>
      <ul className="dash-insights-list">
        {insights.map((i) => (
          <li key={i.id} className={`dash-insight dash-insight--${i.tone}`}>
            <span className="dash-insight-icon" aria-hidden>
              {toneIcon(i.tone)}
            </span>
            <div className="dash-insight-body">
              <div className="dash-insight-row-title">{i.title}</div>
              <p className="dash-insight-text">{i.body}</p>
              {i.to && i.ctaLabel && (
                <Link to={i.to} className="dash-insight-cta">
                  {i.ctaLabel} →
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default QuickInsightsPanel;
